// How everybody did on one puzzle: the two aggregate panels on a result screen
// (CLAUDE.md: the data layer, step 5).
//
// **Asked of the server only once the player has finished**, with their own
// guesses, which the server checks before it counts anything (`puzzle_stats`):
// Call Sheet's first-guess titles name the answer. **And only once this
// device's own result has been dealt with**, so the player is counted in the
// figures they are shown.
//
// Both panels come from one answer, which is what keeps the distribution and
// the title statistics in agreement. Below the minimum number of players the
// server says so and the panels stay hidden.
import { useEffect, useState } from 'react';
import { rpc } from './api.ts';
import { recorded, type GameKey } from './history.ts';
import { MISSED, guessLabel, type DistRow } from '../components/Distribution.tsx';

export interface RunningOrderTitle { id: number; misplaced: number }
export interface CallSheetTitle { kind: 'movie' | 'tv'; id: number; title: string | null; count: number; correct: boolean }

export interface PuzzleStats<T> {
  players: number;
  shown: boolean;
  /** Solved on guess 1, 2 and 3, then missed: counts. */
  distribution?: number[];
  titles?: T[];
}

const SERVER_GAME: Record<GameKey, string> = { 'running-order': 'running_order', 'call-sheet': 'call_sheet' };

/** The panels' figures, or null while they load, fail, or are not wanted. */
export function usePuzzleStats<T>(game: GameKey, number: number, guesses: unknown[], wanted = true): PuzzleStats<T> | null {
  const [stats, setStats] = useState<PuzzleStats<T> | null>(null);
  const key = JSON.stringify(guesses);
  useEffect(() => {
    if (!wanted || guesses.length === 0) return;
    let live = true;
    recorded(game, number)
      .then(() => rpc<PuzzleStats<T>>('puzzle_stats', { p_game: SERVER_GAME[game], p_number: number, p_guesses: guesses }))
      .then((s) => { if (live) setStats(s); }, () => { /* no panels: the screen is complete without them */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, number, key, wanted]);
  return stats;
}

/** A share of the players, as a whole percentage. */
export const pct = (count: number, players: number) => (players > 0 ? Math.round((count / players) * 100) : 0);

/** The distribution chart's rows from the server's counts, the player's own
 *  outcome highlighted. */
export function distributionRows(counts: number[], players: number, own: string): DistRow[] {
  return counts.map((count, i) => {
    const label = i < 3 ? guessLabel(i + 1) : MISSED;
    const share = pct(count, players);
    return { label, value: `${share}%`, pct: share, own: label === own };
  });
}

/** "1 player", "1,644 players". */
export const playersLabel = (n: number) => `${n.toLocaleString('en-GB')} ${n === 1 ? 'player' : 'players'}`;
